import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import supabase from '../config/supabase';
import SEO from '../components/common/SEO';

const { FiMail, FiLock, FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiUserPlus, FiLogIn, FiArrowLeft } = FiIcons;

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastResetRequestTime, setLastResetRequestTime] = useState(0);
  const { navigateTo, currentPathWithoutLang } = useLanguageNavigation();
  const { signUp, signIn, signInWithGoogle, currentUser } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const emailInputRef = useRef(null);

  // 🔐 檢測 Supabase 的密碼重設連結並自動重定向
  useEffect(() => {
    const checkPasswordResetLink = () => {
      const hashFragment = window.location.hash;
      if (hashFragment.includes('access_token=') && hashFragment.includes('type=recovery')) {
        const fragmentParams = new URLSearchParams(hashFragment.substring(1));
        const accessToken = fragmentParams.get('access_token');
        const refreshToken = fragmentParams.get('refresh_token');
        const type = fragmentParams.get('type');
        if (accessToken && type === 'recovery') {
          const newPath = `/#/${language}/reset-password?access_token=${accessToken}&type=${type}${refreshToken ? `&refresh_token=${refreshToken}` : ''}`;
          window.location.replace(newPath);
          return true;
        }
      }
      const queryParams = new URLSearchParams(window.location.search);
      const accessToken = queryParams.get('access_token');
      const type = queryParams.get('type');
      if (accessToken && type === 'recovery') {
        const refreshToken = queryParams.get('refresh_token');
        const newPath = `/#/${language}/reset-password?access_token=${accessToken}&type=${type}${refreshToken ? `&refresh_token=${refreshToken}` : ''}`;
        window.location.replace(newPath);
        return true;
      }
      return false;
    };
    if (!currentUser) {
      checkPasswordResetLink();
    }
  }, [language, currentUser]);

  // 已登入用戶重定向到儀表板
  useEffect(() => {
    if (currentUser) {
      navigateTo('/dashboard', { replace: true });
    }
  }, [currentUser, navigateTo]);

  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }), []);

  const itemVariants = useMemo(() => ({
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, delay: 0.2 } },
  }), []);

  const messageVariants = useMemo(() => ({
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
  }), []);

  const formContent = useMemo(() => {
    const content = {
      login: {
        titleKey: 'auth.signInTitle',
        subtitleKey: 'auth.signInSubtitle',
        buttonKey: 'auth.signInButton',
        switchTextKey: 'auth.noAccount',
        switchLinkKey: 'auth.signUpButton',
        Icon: FiLogIn,
      },
      signup: {
        titleKey: 'auth.signUpTitle',
        subtitleKey: 'auth.signUpSubtitle',
        buttonKey: 'auth.signUpButton',
        switchTextKey: 'auth.alreadyHaveAccount',
        switchLinkKey: 'auth.signInButton',
        Icon: FiUserPlus,
      },
      forgotPassword: {
        titleKey: 'auth.forgotPasswordTitle',
        subtitleKey: 'auth.forgotPasswordSubtitle',
        buttonKey: 'auth.sendResetEmailButton',
        Icon: FiMail,
      },
    };
    return content[mode];
  }, [mode]);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ email: '', password: '', confirmPassword: '' });
    setShowPassword(false);
    clearMessages();
  }, [clearMessages]);

  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    resetForm();
  }, [resetForm]);

  const handleInputChange = useCallback((field) => {
    return (e) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      if (error) setError(null);
    };
  }, [error]);

  const handleEmailChange = useMemo(() => handleInputChange('email'), [handleInputChange]);
  const handlePasswordChange = useMemo(() => handleInputChange('password'), [handleInputChange]);
  const handleConfirmPasswordChange = useMemo(() => handleInputChange('confirmPassword'), [handleInputChange]);

  useEffect(() => {
    if (emailInputRef.current) emailInputRef.current.focus();
  }, [mode]);

  const translateError = useCallback((errorMessage) => {
    if (!errorMessage) return t('auth.unexpectedError');
    if (errorMessage === 'Failed to fetch' || errorMessage.includes('NetworkError')) {
      return t('common.errors.failedToFetch');
    }
    const errorTranslations = {
      'Invalid login credentials': t('auth.invalidLoginCredentials'),
      'User already registered': t('auth.userAlreadyRegistered'),
      'Email not confirmed': t('auth.emailNotConfirmed'),
      'Password should be at least 6 characters': t('auth.passwordTooShort'),
      'Unable to validate email address: invalid format': t('auth.emailInvalid'),
      'Email rate limit exceeded': t('auth.emailRateLimitExceeded'),
      'For security purposes, you can only request this once every 60 seconds': t('auth.securityRateLimit'),
      'User not found': t('auth.userNotFound'),
    };
    if (errorTranslations[errorMessage]) return errorTranslations[errorMessage];
    for (const [key, translation] of Object.entries(errorTranslations)) {
      if (errorMessage.includes(key)) return translation;
    }
    return errorMessage;
  }, [t]);

  const validateForm = useCallback(() => {
    const { email, password, confirmPassword } = formData;
    if (mode === 'forgotPassword') {
      if (!email.trim()) return t('auth.emailRequired');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) return t('auth.emailInvalid');
      return null;
    }
    if (!email.trim()) return t('auth.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return t('auth.emailInvalid');
    if (!password) return t('auth.passwordRequired');
    if (password.length < 6) return t('auth.passwordTooShort');
    if (mode === 'signup' && !confirmPassword) return t('auth.confirmPasswordRequired');
    if (mode === 'signup' && password !== confirmPassword) return t('auth.passwordMismatch');
    return null;
  }, [formData, mode, t]);

  const handleForgotPassword = useCallback(async (e) => {
    e.preventDefault();
    clearMessages();
    const now = Date.now();
    const timeSinceLastRequest = (now - lastResetRequestTime) / 1000;
    if (timeSinceLastRequest < 15 && lastResetRequestTime > 0) {
      setError(t('auth.resetRequestCooldown'));
      return;
    }
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    setIsLoading(true);
    const { email } = formData;
    try {
      const isProduction = window.location.hostname === 'petcarenote.com';
      const redirectUrl = isProduction ? 'https://petcarenote.com' : window.location.origin;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
      if (resetError) throw new Error(translateError(resetError.message));
      setLastResetRequestTime(now);
      setSuccess(t('auth.resetEmailSent'));
      setFormData({ email: '', password: '', confirmPassword: '' });
    } catch (err) {
      setError(translateError(err.message));
    } finally {
      setIsLoading(false);
    }
  }, [formData, clearMessages, validateForm, translateError, t, language, lastResetRequestTime]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (mode === 'forgotPassword') return handleForgotPassword(e);
    clearMessages();
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    setIsLoading(true);
    const { email, password } = formData;
    try {
      if (mode === 'login') {
        const { error: signInError } = await signIn(email, password);
        if (signInError) throw new Error(translateError(signInError.message));
        setSuccess(t('auth.signInSuccess'));
        setTimeout(() => navigateTo('/dashboard'), 1000);
      } else {
        const { user, error: signUpError } = await signUp(email, password);
        if (signUpError) throw new Error(translateError(signUpError.message));
        setSuccess(t('auth.accountCreatedAndSignedIn'));
        setTimeout(() => navigateTo('/dashboard'), 1500);
      }
    } catch (err) {
      setError(translateError(err.message));
    } finally {
      setIsLoading(false);
    }
  }, [mode, formData, clearMessages, validateForm, signIn, signUp, translateError, t, navigateTo, handleForgotPassword]);

  const handleGoogleSignIn = useCallback(async () => {
    clearMessages();
    setIsLoading(true);
    try {
      const { error: googleError } = await signInWithGoogle();
      if (googleError) throw new Error(translateError(googleError.message));
    } catch (err) {
      setError(t('auth.googleSignInError'));
      setIsLoading(false);
    }
  }, [signInWithGoogle, clearMessages, translateError, t]);

  const InputField = useCallback(({ id, type, value, onChange, placeholder, icon, children, inputRef }) => (
    <div className="relative">
      <SafeIcon icon={icon} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isDark ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
        }`}
        required
        disabled={isLoading}
        autoComplete={id === 'email' ? 'email' : id === 'password' ? (mode === 'login' ? 'current-password' : 'new-password') : 'new-password'}
      />
      {children}
    </div>
  ), [isDark, isLoading, mode]);

  return (
    <>
      {/* ✅ noindex: 阻止搜尋引擎索引登入/註冊頁面 */}
      <SEO
        title="Sign In | Pet Care Note"
        description="Sign in to Pet Care Note to manage your pet's health records."
        noindex={true}
      />
      <div className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className={`w-full max-w-md rounded-xl shadow-lg p-8 space-y-6 ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          <motion.div variants={itemVariants} className="text-center">
            <div className={`inline-block p-3 rounded-full mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <SafeIcon icon={formContent.Icon} className="w-8 h-8 text-blue-500" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(formContent.titleKey)}
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t(formContent.subtitleKey)}
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div key="error" variants={messageVariants} initial="hidden" animate="visible" exit="exit"
                className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <SafeIcon icon={FiAlertCircle} className="mr-3 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}
            {success && (
              <motion.div key="success" variants={messageVariants} initial="hidden" animate="visible" exit="exit"
                className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
                <SafeIcon icon={FiCheckCircle} className="mr-3 flex-shrink-0" />
                <span className="text-sm">{success}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {mode === 'login' && (
            <motion.div variants={itemVariants} className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={handleGoogleSignIn} type="button" disabled={isLoading}
                className={`w-full flex items-center justify-center gap-3 px-4 py-3 border rounded-lg font-medium transition-all ${
                  isDark ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </motion.button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-2 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                    {t('auth.orDivider')}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-4">
            {(mode === 'login' || mode === 'signup' || mode === 'forgotPassword') && (
              <InputField inputRef={emailInputRef} id="email" type="email" value={formData.email}
                onChange={handleEmailChange} placeholder={t('auth.emailPlaceholder')} icon={FiMail} />
            )}
            {(mode === 'login' || mode === 'signup') && (
              <InputField id="password" type={showPassword ? 'text' : 'password'} value={formData.password}
                onChange={handlePasswordChange} placeholder={t('auth.passwordPlaceholder')} icon={FiLock}>
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                    isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                </button>
              </InputField>
            )}
            {mode === 'login' && (
              <div className="flex justify-end">
                <button type="button" onClick={() => switchMode('forgotPassword')} disabled={isLoading}
                  className="text-sm font-medium text-blue-500 hover:text-blue-600 hover:underline transition-colors">
                  {t('auth.forgotPassword')}
                </button>
              </div>
            )}
            <AnimatePresence>
              {mode === 'signup' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
                  <InputField id="confirmPassword" type="password" value={formData.confirmPassword}
                    onChange={handleConfirmPasswordChange} placeholder={t('auth.confirmPasswordPlaceholder')} icon={FiLock} />
                </motion.div>
              )}
            </AnimatePresence>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
              className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
              disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {mode === 'login' && t('auth.signingYouIn')}
                  {mode === 'signup' && t('auth.creatingAccount')}
                  {mode === 'forgotPassword' && t('auth.sendingResetEmail')}
                </div>
              ) : t(formContent.buttonKey)}
            </motion.button>
          </motion.form>

          {mode === 'forgotPassword' && (
            <motion.div variants={itemVariants} className="text-center">
              <button onClick={() => switchMode('login')} disabled={isLoading}
                className={`flex items-center justify-center gap-2 mx-auto text-sm font-medium transition-colors ${
                  isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}>
                <SafeIcon icon={FiArrowLeft} />
                {t('auth.backToSignIn')}
              </button>
            </motion.div>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <motion.p variants={itemVariants} className={`text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t(formContent.switchTextKey)}{' '}
              <button onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')} disabled={isLoading}
                className="font-medium text-blue-500 hover:text-blue-600 hover:underline transition-colors">
                {t(formContent.switchLinkKey)}
              </button>
            </motion.p>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default AuthPage;